import { getTaskAudioUrl } from '../../api';

/**
 * Форматтеры карточки звонка. Живут отдельно от компонента, потому что теми же
 * значениями подписан журнал в `CallDetails`, и расхождение между таблицей и
 * раскрытой карточкой выглядело бы как расхождение данных.
 */

export const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const SHORT_DATE_FORMAT = { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };

/** Тот же момент, что и `formatDate`, без года и секунд: для коротких списков, ведущих в журнал. */
export const formatShortDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(undefined, SHORT_DATE_FORMAT);
};

export const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/** Цитаты нод склеены через `|`; повторы убираем, иначе одна фраза печатается дважды. */
export const formatEvidence = (value) => {
  if (!value) return '';

  const chunks = String(value)
    .split(/\s*\|\s*/g)
    .map((part) => part.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

  const seen = new Set();
  const unique = [];
  for (const chunk of chunks) {
    const key = chunk.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(chunk);
  }

  return unique.join(' | ');
};

export const formatOperatorTotal = (value) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  return Number.isNaN(numeric) ? '—' : `${numeric.toFixed(1)}%`;
};

export const resolveChecklistScoreStyle = (score) => {
  // null — это «Н/П», а не ноль: пункт неприменим к звонку и в среднюю оценку не входит.
  if (score === null || score === undefined) {
    return { symbol: 'н/п', className: 'text-muted-foreground' };
  }

  const numeric = Number(score);
  if (Number.isNaN(numeric)) {
    return { symbol: '—', className: 'text-muted-foreground' };
  }
  if (numeric >= 1) {
    return numeric > 1
      ? { symbol: numeric.toFixed(1), className: 'text-emerald-600' }
      : { symbol: '✓', className: 'text-emerald-600' };
  }
  if (numeric >= 0.5) {
    return { symbol: 'частично', className: 'text-yellow-600' };
  }
  return { symbol: '✗', className: 'text-red-500' };
};

const checklistRank = (item) => (Number.isFinite(item?.order) ? item.order : Number.POSITIVE_INFINITY);

/**
 * Пункты чек-листа в порядке показа: порядок пакета, перебитый настройкой в «Узлах анализа».
 * Сервер отдаёт позицию полем `order`, и сортировать надо по нему, а не полагаться на порядок
 * ключей объекта: ключи пунктов — произвольные строки, и числовые (`"2"`, `"10"`) браузер
 * переставляет сам. Пункты без `order` (legacy-чек-лист) идут после упорядоченных, как пришли.
 */
export const orderChecklistEntries = (checklist) =>
  Object.entries(checklist ?? {})
    .map((entry, index) => ({ entry, index, rank: checklistRank(entry[1]) }))
    .sort((a, b) => (a.rank === b.rank ? a.index - b.index : a.rank - b.rank))
    .map(({ entry }) => entry);

export const formatTranscriptLines = (transcript) => {
  if (!transcript) {
    return [];
  }

  const normalized = transcript.replace(/\r\n/g, '\n');
  const speakerChunks = normalized.match(/\[Спикер [AB]\][^[]*/gi);
  const chunks = speakerChunks && speakerChunks.length ? speakerChunks : normalized.split('\n');

  return chunks
    .map((line) => line.trim())
    .filter(Boolean);
};

export const formatDetailsSubtitle = (call) => {
  if (!call) return '';
  const parts = [
    formatDate(call.callStartedAt),
    call.employeeName || '—',
    formatDuration(call.durationSeconds)
  ].filter(Boolean);
  return parts.join(' • ');
};

/**
 * Относительный `/api/tasks/{id}/audio` из аналитики не несёт базового URL,
 * поэтому как есть берём только абсолютный адрес, а иначе собираем ссылку сами.
 * `callId` здесь и есть `taskId`.
 */
export const resolveRecordingUrl = (call) => {
  if (!call) return null;
  const trimmed = call.recordingUrl?.trim();
  if (trimmed?.startsWith('http')) return trimmed;
  return getTaskAudioUrl(call.callId);
};
